/**
 * An opportunity, exactly as `GET /opportunities` serves it.
 *
 * Mirrors `OpportunitySchema` in commonground-backend
 * (src/features/opportunities/schemas/index.ts) field for field; the contract
 * is its docs/api/opportunities.md. "Event" is still the word on screen —
 * "opportunity" is only the record's name.
 *
 * What the interface needs but the record does not carry — the day an event
 * falls on, its start and end as minutes after midnight — is worked out here
 * from `startsAt`, `endsAt` and `timezone`, once per record, and never stored.
 */

export type OpportunityFormat = 'in_person' | 'virtual' | 'hybrid' | 'see_listing';

/**
 * How you get in, on one axis: how much permission it takes.
 *
 *   open_to_public         walk in. No registration of any kind.
 *   registration_required  anyone may come, but you must register first.
 *   apply_to_attend        registration exists and a human approves it.
 *   invite_only            not openly registerable at all.
 *   paid                   a ticket must be bought. Orthogonal to the rest, and
 *                          kept separate because cost is its own barrier.
 *   see_listing            the source gave no signal. Honest, not a default.
 */
export type RsvpType =
  | 'open_to_public'
  | 'registration_required'
  | 'apply_to_attend'
  | 'invite_only'
  | 'paid'
  | 'see_listing';

/** What `address` holds. Only `address` is a real street address. */
export type LocationType =
  | 'address'
  | 'online'
  | 'after_registration'
  | 'tbd'
  | 'multiple_venues'
  | 'neighborhood_only'
  | 'see_listing';

export interface VocabularyRef {
  key: string;
  label: string;
  icon: string | null;
}

export interface Opportunity {
  id: string;
  sourceProvider: string;
  sourceUrl: string;
  registrationUrl: string | null;
  organizationId: string | null;
  organizationName: string;
  name: string;
  /** The source's own short text, for cards. */
  summary: string | null;
  /** Markdown for the callout. */
  description: string | null;
  /** ISO-8601 instant. */
  startsAt: string;
  /** ISO-8601 instant, or null when the source published no end. */
  endsAt: string | null;
  /** No published time, or a programme running across several days. */
  isAllDay: boolean;
  /** IANA zone the times were published in. */
  timezone: string | null;
  format: OpportunityFormat;
  /** The word on the stub, e.g. "PANEL". */
  eventType: string | null;
  rsvpType: RsvpType | null;
  category: string | null;
  /** Strongest match first. */
  topics: VocabularyRef[];
  goals: VocabularyRef[];
  /** "Manhattan - Midtown", a bare borough, or null. */
  neighborhood: string | null;
  locationType: LocationType | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  isOutdoor: boolean | null;
  isOfficial: boolean;
  isPaid: boolean | null;
  priceAmount: number | null;
  priceCurrency: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OpportunityPage {
  items: Opportunity[];
  /** Opaque. Null when nothing follows this page. */
  nextCursor: string | null;
  /** Opaque. Null when no matching event sorts before this page. */
  prevCursor: string | null;
}

export interface VocabularyEntry extends VocabularyRef {
  sortOrder: number;
}

export interface Taxonomy {
  topics: VocabularyEntry[];
  goals: VocabularyEntry[];
  /** Every event-type word, in display order. */
  eventTypes: string[];
}

/** Personal events are namespaced so a backend id can never collide with one. */
export const PERSONAL_PREFIX = 'personal:';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Backend ids are UUIDs. A personal event or a pre-migration id is not the API's to answer. */
export const isOpportunityId = (id: string): boolean => UUID.test(id);

// --- time ----------------------------------------------------------------------

/** What the Airtable source is curated in, and what a record with no zone is read as. */
export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * The length drawn for an event with no published end.
 *
 * A display rule, not data: `endsAt` stays null, and everywhere that draws the
 * hour says it was assumed.
 */
export const DEFAULT_DURATION_MINUTES = 60;

export interface WallClock {
  /** "2026-09-22", on the calendar of the zone it was read in. */
  dayKey: string;
  /** Minutes after that day's midnight. */
  minutes: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** An instant, as a date and a clock reading in `timeZone`. */
export function wallClock(at: Date | string, timeZone: string = DEFAULT_TIMEZONE): WallClock {
  const part: Record<string, string> = {};
  for (const { type, value } of formatterFor(timeZone).formatToParts(new Date(at))) {
    part[type] = value;
  }
  return {
    dayKey: `${part.year}-${part.month}-${part.day}`,
    minutes: (Number(part.hour) % 24) * 60 + Number(part.minute),
  };
}

const utcDay = (dayKey: string) => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

export const daysBetween = (from: string, to: string): number =>
  Math.round((utcDay(to) - utcDay(from)) / 86_400_000);

export function addDays(dayKey: string, days: number): string {
  return new Date(utcDay(dayKey) + days * 86_400_000).toISOString().slice(0, 10);
}

/** How far `timeZone` is from UTC at an instant, in milliseconds. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const { dayKey, minutes } = wallClock(new Date(utcMs), timeZone);
  const date = new Date(utcMs);
  const seconds = date.getUTCSeconds() * 1000 + date.getUTCMilliseconds();
  return utcDay(dayKey) + minutes * 60_000 + seconds - utcMs;
}

/**
 * A wall-clock reading in `timeZone` as an ISO instant — the inverse of
 * `wallClock`.
 *
 * Two passes: the offset depends on the instant being resolved, and the second
 * pass settles a time that crossed a daylight-saving boundary on the first.
 */
export function zonedIso(dayKey: string, minutes: number, timeZone: string = DEFAULT_TIMEZONE): string {
  const wall = utcDay(dayKey) + minutes * 60_000;
  let utcMs = wall - zoneOffsetMs(wall, timeZone);
  utcMs = wall - zoneOffsetMs(utcMs, timeZone);
  return new Date(utcMs).toISOString();
}

interface Derived {
  localDate: string;
  startMinute: number | null;
  endMinute: number | null;
}

/**
 * Read once per record. The day column lays itself out on every drag frame,
 * and `Intl` is not free. Records are never mutated — an edit makes a new one —
 * so the object itself is a safe key.
 */
const derived = new WeakMap<Opportunity, Derived>();

function derive(o: Opportunity): Derived {
  const cached = derived.get(o);
  if (cached) return cached;

  const zone = o.timezone ?? DEFAULT_TIMEZONE;
  const start = wallClock(o.startsAt, zone);
  let value: Derived;

  if (o.isAllDay) {
    value = { localDate: start.dayKey, startMinute: null, endMinute: null };
  } else {
    let end = start.minutes + DEFAULT_DURATION_MINUTES;
    if (o.endsAt) {
      // Counted from the start day, so an end past midnight is after 1440
      // rather than wrapping round to the small hours of the same day.
      const at = wallClock(o.endsAt, zone);
      end = at.minutes + daysBetween(start.dayKey, at.dayKey) * 24 * 60;
    }
    value = { localDate: start.dayKey, startMinute: start.minutes, endMinute: end };
  }

  derived.set(o, value);
  return value;
}

/** The day the event starts on, in its own zone. */
export const localDate = (o: Opportunity): string => derive(o).localDate;

/** Minutes after midnight it starts; null for all-day events. */
export const startMinute = (o: Opportunity): number | null => derive(o).startMinute;

/**
 * Minutes after midnight it ends: as published, or an assumed hour after the
 * start when no end was. Null for all-day events.
 */
export const endMinute = (o: Opportunity): number | null => derive(o).endMinute;

/** False when the end was assumed rather than published. */
export const hasEndTime = (o: Opportunity): boolean => o.endsAt != null;

/**
 * Over: its end has gone by. An event with no published end is over an hour
 * after it starts, and an all-day one lasts until midnight — the same rule the
 * backend's listing floor applies.
 */
export function isPast(o: Opportunity, now: WallClock): boolean {
  const day = localDate(o);
  if (day !== now.dayKey) return day < now.dayKey;
  const end = endMinute(o);
  return end != null && end <= now.minutes;
}

/** Day, then all-day first, then start, then name — the order the API lists in. */
const collator = new Intl.Collator('en', { sensitivity: 'base' });

export function compareOpportunities(a: Opportunity, b: Opportunity): number {
  return (
    localDate(a).localeCompare(localDate(b)) ||
    Number(b.isAllDay) - Number(a.isAllDay) ||
    (startMinute(a) ?? -1) - (startMinute(b) ?? -1) ||
    collator.compare(a.name, b.name) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

// --- labels ----------------------------------------------------------------------

/** The words the site has always shown. "Virtual" reads "Online" where a component says so. */
export const FORMAT_LABEL: Record<OpportunityFormat, string> = {
  in_person: 'In person',
  virtual: 'Virtual',
  hybrid: 'Hybrid',
  see_listing: 'See Listing',
};

export const formatLabel = (format: OpportunityFormat): string =>
  FORMAT_LABEL[format] ?? FORMAT_LABEL.see_listing;

export const RSVP_LABEL: Record<RsvpType, string> = {
  open_to_public: 'Open to Public',
  registration_required: "Public (RSVP Req'd)",
  apply_to_attend: 'Apply to Attend',
  invite_only: 'Invite Only',
  paid: 'Paid',
  see_listing: 'See Listing',
};

/** A row the source said nothing about reads as See Listing, which is what it means. */
export const rsvpLabel = (rsvp: RsvpType | null): string => RSVP_LABEL[rsvp ?? 'see_listing'];

/**
 * The venue line for anything that is not a street address.
 *
 * Fixed wording rather than the source's own text, which the backend keeps in
 * `address` for these: the site has always said it this way.
 */
export const LOCATION_LABEL: Record<Exclude<LocationType, 'address'>, string> = {
  online: 'Online',
  after_registration: 'Location provided after registration',
  tbd: 'Location TBD',
  multiple_venues: 'Multiple venues',
  neighborhood_only: 'Neighborhood only',
  see_listing: 'See Listing',
};

export const isOnline = (o: Opportunity): boolean => o.format === 'virtual';

/** A real address, so worth a Google Maps link. */
export const isMappable = (o: Opportunity): boolean =>
  o.locationType === 'address' && Boolean(o.address);

export const mapsQueryOf = (o: Opportunity): string | null => (isMappable(o) ? o.address : null);

export const coordsOf = (o: Opportunity): [number, number] | null =>
  o.latitude != null && o.longitude != null ? [o.latitude, o.longitude] : null;

/**
 * The venue line: the address, or what stands in for one.
 *
 * A record with no location type is either a personal event, whose free text is
 * shown as typed, or a row ingested before the backend classified locations.
 */
export function venueText(o: Opportunity): string {
  if (isMappable(o)) return o.address as string;
  if (o.locationType && o.locationType !== 'address') return LOCATION_LABEL[o.locationType];
  if (o.id.startsWith(PERSONAL_PREFIX)) return o.address || 'No location given';
  return LOCATION_LABEL.see_listing;
}

/** Where the title and "Register" go. */
export const eventUrl = (o: Opportunity): string => o.registrationUrl || o.sourceUrl;
